import { useState, useMemo } from "react"
import { PickerWithType } from "../../components/DatePicker"
import { Card, CardBody, CardTitle } from "../../components/Card"
import { componentType } from "../../components/Chart"
import { Label, SelectInput } from "../../components/Form"
import { Column } from "../../components/Layout"
import { LoadingWithLabel } from "../../components/Loading"
import { ModalBody, ModalFooter, ModalHeader, ModalPanel } from "../../components/Modal"
import { GrFormClose } from "react-icons/gr"
import { TiFilter } from "react-icons/ti"
import { Button } from "../../components/Button"
import { PivotTable } from "../../components/Dashboard"
import "./styles.scss"
import { EmptyGraph } from "../../components/Empty"
import Overlay from "../../components/Overlay"

const DynamicDashScreen = ({
  modalIsOpen,
  setModalIsOpen,
  dw,
  dwReference,
  filter,
  handleFilter,
  loadingData,
  isUserAdmin,
  users,
  usersTec,
  scope,
  isShortcutsModalOpen,
  setIsShortcutsModalOpen,
  shortcuts,
  handleApplyShortcut,
  appliedShortcut,
  handleResetShortcut,
  handleOpenDashComparison,
  comparisonFields,
  dwComparison,
  formsByProject,
  // Novas props para busca manual
  handleSearchData,
  hasFiltersChanged,
  cachedData,
  isIframeMode,
}) => {
  const [showAllLabels, setShowAllLabels] = useState(false)

  // Função para extrair labels únicos de ambos os períodos para sincronização de cores
  const getUniqueLabels = useMemo(() => {
    const allLabels = new Set()

    // Extrair labels do período atual (usar dw original, não processedDw)
    dw?.forEach(dashboard => {
      dashboard?.components?.forEach(component => {
        component?.fields?.forEach(field => {
          if (field[component.stackedBy]) {
            allLabels.add(field[component.stackedBy])
          }
        })
      })
    })

    // Extrair labels do período de referência
    dwReference?.forEach(dashboard => {
      dashboard?.components?.forEach(component => {
        component?.fields?.forEach(field => {
          if (field[component.stackedBy]) {
            allLabels.add(field[component.stackedBy])
          }
        })
      })
    })

    return Array.from(allLabels).sort()
  }, [dw, dwReference])

  // Função para filtrar apenas a última ocorrência de cada produtor
  const filterLatestOccurrence = (
    componentType = null,
    hasFilter = false,
    fields,
    stackedBy,
    dateField = "Inicio"
  ) => {
    if (!fields) return fields;

    const isTable = componentType === "table";
    const isTableWithoutFilters = isTable && !hasFilter;
    const isTableWithFilters = isTable && hasFilter;

    const getDate = (field) =>
      new Date(
        field[dateField] ||
        field["Inicio"] ||
        field["inicio"] ||
        field["data"] ||
        field["Data"] ||
        "1900-01-01"
      );

    // Caso 1: tabela com filtros => retorna os dados sem modificar
    if (isTableWithFilters) {
      console.log("Caso 1 (table with filters) - returning original:", fields.length);
      return fields;
    }

    // Caso 2: tabela sem filtros => mescla último valor por campo (merge) (mantém comportamento anterior)
    if (isTableWithoutFilters) {
      const groupedByProducer = {};

      fields.forEach((field, idx) => {
        const producerId = field["ProdutorId"] || field["id"];
        const producerName = field[stackedBy] || field["Produtor"] || field["produtor"];
        const parsedDate = getDate(field);

        const key = producerId && producerName
          ? `${producerId}_${producerName}`
          : `__noid__${idx}`;

        if (!groupedByProducer[key]) groupedByProducer[key] = [];
        groupedByProducer[key].push({ ...field, _parsedDate: parsedDate, _origIndex: idx });
      });

      // Ordena cada grupo por data desc, mas mantém todos
      const allPreserved = Object.values(groupedByProducer)
        .flatMap(group => group.sort((a, b) => b._parsedDate - a._parsedDate))
        .map(({ _parsedDate, _origIndex, ...rest }) => rest);

      return allPreserved;
    }

    const groupedByProducer = {};

    fields.forEach((field, idx) => {
      const producerId = field["ProdutorId"] || field["id"];
      const producerName = field[stackedBy] || field["Produtor"] || field["produtor"];
      const parsedDate = getDate(field);

      const key = producerId && producerName
        ? `${producerId}_${producerName}`
        : `__noid__${idx}`;

      if (!groupedByProducer[key]) groupedByProducer[key] = [];
      groupedByProducer[key].push({ ...field, _parsedDate: parsedDate, _origIndex: idx });
    });

    const allPreserved = [];
    Object.values(groupedByProducer).forEach(group => {
      group.sort((a, b) => b._parsedDate - a._parsedDate);
      allPreserved.push(...group);
    });

    return allPreserved;
  };

  const formatSmartPeriod = (periodStr) => {
    if (!periodStr) return null;

    const [start, end] = periodStr.split("|");

    const [startY, startM, startD] = start.split("-");
    const [endY, endM, endD] = end.split("-");

    const sameYear = startY === endY;
    const sameMonth = startM === endM && sameYear;

    if (sameMonth) {
      return `${startD} - ${endD}/${endM}/${endY}`;
    }

    if (sameYear) {
      return `${startD}/${startM} - ${endD}/${endM}/${endY}`;
    }

    return `${startD}/${startM}/${startY} - ${endD}/${endM}/${endY}`;
  }

  const periodDate = formatSmartPeriod(filter?.date);
  const referenceDate = formatSmartPeriod(filter?.reference);

  const hasFilter = filter?.tec?.length > 0 || filter?.producer?.length > 0 || filter?.date || filter?.reference

  // Processar dados para mostrar apenas última ocorrência no gráfico atual (direita)
  const processedDw = useMemo(() => {
    if (!dw) return dw
    return dw.map(dashboard => ({
      ...dashboard,
      components: dashboard.components?.map(component => ({
        ...component,
        fields: filterLatestOccurrence(component?.type, hasFilter, component.fields, component.stackedBy)
      }))
    }))
  }, [dw, hasFilter])

  return (
    <>
      {/* <Overlay loading={loadingData} /> */}
      {!isIframeMode && (
        <div className="dynamic-dash-filter">
          {scope?.id === "92a49983-ca3d-49d5-9a64-8fc445e1777d" && (
            <div className="dynamic-button-all" onClick={() => setShowAllLabels((prev) => !prev)}>
              {showAllLabels ? "Ocultar Labels" : "Mostrar Labels"}
            </div>
          )}
          <div className="dynamic-dash-filter-selection">
            <div className="filter-fields">
              <Label label="Filtrar por Produtor">
                <SelectInput
                  isMulti
                  whiteVariation
                  isSearchable={true}
                  onChange={(value) => handleFilter(value, "producer")}
                  options={users}
                />
              </Label>
              <Label label="Filtrar por Técnico">
                <SelectInput
                  isMulti
                  whiteVariation
                  isSearchable={true}
                  onChange={(value) => handleFilter(value, "tec")}
                  options={usersTec}
                />
              </Label>
              <Label label="Filtrar por Formulário">
                <SelectInput
                  isMulti
                  whiteVariation
                  isSearchable={true}
                  onChange={(value) => handleFilter(value, "form")}
                  options={formsByProject.map((row) => ({ label: row.title, value: row.id }))}
                />
              </Label>

              <Label label="Data Anterior (Referência)">
                {Array.isArray(shortcuts) && shortcuts.length ? (
                  <div
                    className="show-shortcuts"
                    onClick={() => setIsShortcutsModalOpen("referenceForm")}>
                    <TiFilter size={24} color="#879392" />
                  </div>
                ) : (
                  <></>
                )}
                <PickerWithType
                  type={filter?.period}
                  onChange={(value) => handleFilter(value, "reference")}
                  defaultValue={filter?.reference}
                />
                {appliedShortcut?.referenceForm && (
                  <div className="applied-shortcut">
                    {appliedShortcut?.referenceForm}
                    <GrFormClose
                      size={16}
                      className="icon"
                      onClick={() => handleResetShortcut("referenceForm")}
                    />
                  </div>
                )}
              </Label>

              <Label label="Data Atual">
                {Array.isArray(shortcuts) && shortcuts.length ? (
                  <div className="show-shortcuts" onClick={() => setIsShortcutsModalOpen("form")}>
                    <TiFilter size={24} color="#879392" />
                  </div>
                ) : (
                  <></>
                )}
                <PickerWithType
                  type={filter?.period}
                  onChange={(value) => handleFilter(value, "date")}
                  defaultValue={filter?.date}
                />
                {appliedShortcut?.form && (
                  <div className="applied-shortcut">
                    {appliedShortcut?.form}
                    <GrFormClose size={16} className="icon" onClick={() => handleResetShortcut("form")} />
                  </div>
                )}
              </Label>
            </div>

            {/* Botão de Filtrar */}
            <div className="filter-button-container">
              <Button
                color={hasFiltersChanged ? "orange" : "gray"}
                label={loadingData ? "Carregando..." : "Buscar"}
                disabled={loadingData}
                handleClick={handleSearchData}
              />
            </div>
          </div>
        </div>
      )}

      {/* Última atualização */}
      {!isIframeMode && cachedData && (
        <div className="last-update-info">
          <small>
            Última atualização: {new Date(cachedData.timestamp).toLocaleString()}
          </small>
        </div>
      )}

      {loadingData ? (
        <LoadingWithLabel labelLoading="Aguarde, buscando dados..." />
      ) : dw.length > 0 && dw[0]?.isIframe ? (
        // Mostrar iframe se o primeiro item tem isIframe: true
        <div className="iframe-container">
          <iframe
            src={dw[0].link}
            width="100%"
            height="800px"
            allowFullScreen
            title="Dashboard Externo"
            style={{ border: 'none' }}
          />
        </div>
      ) : (
        dw.length > 0 &&
        processedDw.map((item, dwIndex) => (
          item?.components?.length > 0 && (
            <Card className="card-dynamic-dash-container" key={`card-${dwIndex}`}>
              <CardTitle title={item?.step} />
              <CardBody>
                <div className="card-dynamic-dash__content">
                  {item.components.map((component, index) => {
                    const referenceComponent = dwReference?.[dwIndex]?.components?.find(
                      ref => ref?.title === component.title || ref?.value === component.value
                    );

                    return (
                      <Column
                        key={`graph-${dwIndex}.${index}`}
                        className={`col-${dwReference.length > 0
                          ? 12
                          : component.col
                          } chart`}
                        style={{ height: "12px !important" }}
                      >
                        <Card>
                          <div className="cards-component">
                            {referenceDate && periodDate && componentType(referenceComponent?.type || "empty", {
                              data: {
                                ...referenceComponent,
                                fields: filterLatestOccurrence(referenceComponent?.type, hasFilter, referenceComponent?.fields, referenceComponent?.stackedBy)
                              },
                              backgroundColor: index % 2 === 0 ? "#DFE4E1" : undefined,
                              keyWord: referenceComponent?.title || component?.title,
                              value: "VALUE",
                              col: referenceComponent?.col || 6,
                              filterAggregationType: filter?.aggregationType?.value,
                              period: referenceDate,
                              periodReference: periodDate,
                              showAllLabels,
                              isRef: true,
                              showEmptyRef: true,
                              onFilter: (field, value) => handleFilter(value, field),
                              uniqueLabels: getUniqueLabels,
                              syncColors: true,
                            })}
                            {componentType(component.type, {
                              data: item.components[index] || component,
                              backgroundColor: index % 2 === 0 ? "#DFE4E1" : undefined,
                              keyWord: component.title,
                              value: "VALUE",
                              col: component?.col || 6,
                              filterAggregationType: filter?.aggregationType?.value,
                              period: periodDate,
                              showAllLabels,
                              isRef: false,
                              onFilter: (field, value) => handleFilter(value, field),
                              uniqueLabels: getUniqueLabels,
                              syncColors: true,
                              distinct: component.distinct,
                            })}
                          </div>
                          {
                            <div className="cards-dash-button">
                              <Button
                                color="orange"
                                label="comparar"
                                variant="outline"
                                handleClick={() =>
                                  handleOpenDashComparison(component, referenceComponent)
                                }
                              />
                            </div>
                          }
                        </Card>
                      </Column>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          )
        ))
      )}


      {modalIsOpen && (
        <ModalPanel modalIsOpen={modalIsOpen} shouldCloseOnOverlayClick>
          <ModalHeader closeModal={() => setModalIsOpen(false)} title="Tabela de Análise" />
          <ModalBody>
            <div className="table-modal">
              <PivotTable data={comparisonFields} dw={dwComparison} />
            </div>
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalPanel>
      )}
      <ModalPanel modalIsOpen={isShortcutsModalOpen} shouldCloseOnOverlayClick>
        <ModalHeader closeModal={() => setIsShortcutsModalOpen(false)} title="Aplicar Filtro" />
        <ModalBody>
          <div className="modal-buttons-group">
            <div className="modal-buttons">
              {Array.isArray(shortcuts) &&
                shortcuts.length &&
                shortcuts.map((shortcut) => (
                  <button
                    className="shortcut-chip__container"
                    onClick={() => handleApplyShortcut(shortcut)}>
                    {shortcut?.title}
                  </button>
                ))}
            </div>
          </div>
        </ModalBody>
      </ModalPanel>
    </>
  )
}

export default DynamicDashScreen
